import { useState, useEffect } from "react";
import { TrendingUp, BarChart } from "lucide-react";
import StandardSection from "./ui/StandardSection";
import { useScrollAnimation, useStaggeredAnimation } from "@/hooks/useScrollAnimation";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";

// Define stat structure
interface Stat {
  value: string;
  label: string;
  color: string;
}

// Static config for stat colors
const statColors = ["text-primary-600", "text-forest-custom"];

interface ROIUSPProps {
  isExploreEnabled: boolean;
  isLoginEnabled: boolean;
  base_path: string;
}

// Fallback values
const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800";
const DEFAULT_ALT_TEXT = "Digital Marketing Analytics Dashboard";

export default function ROIUSP({ isExploreEnabled, isLoginEnabled, base_path }: ROIUSPProps) {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const { ref: imageRef, isVisible: imageVisible } = useStaggeredAnimation(200);

  // State for dynamic content
  const [title, setTitle] = useState("");
  const [primaryHeader, setPrimaryHeader] = useState("");
  const [secondaryHeader, setSecondaryHeader] = useState("");
  const [description1, setDescription1] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [description2, setDescription2] = useState("");
  const [statsList, setStatsList] = useState<Stat[]>([]);
  // Initialize as empty string
  const [sectionImage, setSectionImage] = useState("");

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const value = data.find((page: any) => page.name === "Landing Page");

        if (!value) {
          throw new Error('Landing page content not found');
        }

        const roiPanel = value?.fields?.find((field: any) => field.key === "roiOptimizationPanel");

        if (roiPanel) {
          // Set text content
          setTitle(roiPanel.components?.find((c: any) => c.key === "roi_title")?.defaultValue || "");
          setPrimaryHeader(roiPanel.components?.find((c: any) => c.key === "roi_primary_header")?.defaultValue || "");
          setSecondaryHeader(roiPanel.components?.find((c: any) => c.key === "roi_secondary_header")?.defaultValue || "");
          setDescription1(roiPanel.components?.find((c: any) => c.key === "roi_description1")?.defaultValue || "");
          setSubheadline(roiPanel.components?.find((c: any) => c.key === "roi_subheadline")?.defaultValue || "");
          setDescription2(roiPanel.components?.find((c: any) => c.key === "roi_description2")?.defaultValue || "");

          // Build stats list
          const stat1Panel = roiPanel.components?.find((c: any) => c.key === "roiStat1Panel");
          const stat2Panel = roiPanel.components?.find((c: any) => c.key === "roiStat2Panel");

          const newStats: Stat[] = [];

          if (stat1Panel) {
            const value = stat1Panel.components?.find((c: any) => c.key === "roi_stat1_value")?.defaultValue || "";
            const label = stat1Panel.components?.find((c: any) => c.key === "roi_stat1_label")?.defaultValue || "";
            // Add formatting for display
            if (value && label) newStats.push({ value: `${value}%`, label, color: statColors[0] });
          }

          if (stat2Panel) {
            const value = stat2Panel.components?.find((c: any) => c.key === "roi_stat2_value")?.defaultValue || "";
            const label = stat2Panel.components?.find((c: any) => c.key === "roi_stat2_label")?.defaultValue || "";
            // Add formatting for display (e.g., 'x' for multiplier)
            if (value && label) newStats.push({ value: `${value}x`, label, color: statColors[1] });
          }

          setStatsList(newStats);

          // Find and set the section image
          const imageGrid = roiPanel.components?.find((c: any) => c.key === "roiImageGrid");
          const imageFileData = imageGrid?.defaultValue?.[0]?.roiImage;

          if (imageFileData?.length > 0) {
            const imageFile = imageFileData[0];
            const imgUrl = imageFile.url
              ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${imageFile.url}`
              : imageFile.data?.url
                ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${imageFile.data.url}`
                : null;

            console.log("sectionimage in roiusp-->", imgUrl);
            if (imgUrl) {
              setSectionImage(imgUrl);
            } else {
              setSectionImage(FALLBACK_IMAGE); // Set to empty, fallback will be used at render
            }
          }
        }
      } catch (error) {
        console.error("Error fetching content for ROIUSP:", error);
        // On error, set to empty
        setTitle("");
        setPrimaryHeader("");
        setSecondaryHeader("");
        setDescription1("");
        setSubheadline("");
        setDescription2("");
        setStatsList([]);
        setSectionImage(FALLBACK_IMAGE); // Set to empty, fallback will be used at render
      }
    };

    fetchContent();
  }, []);

  // Button logic
  const shouldShowButton =
    (isExploreEnabled && isLoginEnabled) ||
    (!isExploreEnabled && isLoginEnabled && isLoggedIn) ||
    (!isLoginEnabled && !isLoggedIn);

  const handleButtonClick = () => {
    localStorage.setItem('clearStartCampaignFilters', 'true');
    if (!isLoggedIn) {
      navigate(`${base_path}/explore`);
    } else {
      navigate(`${base_path}/propertylist`);
    }
  };

  return (
    <>
      <StandardSection background="white">
        <div
          ref={sectionRef}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center px-4 sm:px-0 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <div
            ref={imageRef}
            className={`order-2 lg:order-1 relative transition-all duration-1000 ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
          >
            {/* Image with modern frame */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-forest-custom/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 animate-pulse"></div>
              <img
                // Apply fallback at render time
                src={sectionImage || FALLBACK_IMAGE}
                alt={"Digital Marketing Analytics Dashboard"}
                className="relative w-full rounded-3xl shadow-2xl border border-primary-200/50 group-hover:transform group-hover:scale-110 group-hover:rotate-1 transition-all duration-700 hover:shadow-emerald-200/50"
              />
              {/* Floating accent */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-primary-600 to-forest-custom rounded-full shadow-lg animate-bounce"></div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-forest-100 to-primary-100 border border-forest-200 text-forest-700 px-6 py-3 rounded-full text-sm  mb-8 shadow-sm">
              <TrendingUp className="h-4 w-4 text-forest-600" />
              <span className="text-lg sm:text-xl">{title}</span>
              <BarChart className="h-4 w-4 text-primary-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
              <span className="text-gray-900 block">{primaryHeader}</span>
              <span className="bg-gradient-to-r from-primary-600 to-forest-custom text-primary-600 bg-clip-text block">{secondaryHeader}</span>
            </h2>

            <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto leading-normal mb-6 sm:mb-8">
              {description1}
            </p>

            <div className="text-xl sm:text-2xl md:text-3xl text-gray-800 font-normal mb-4 sm:mb-6">
              {subheadline}
            </div>

            <p className="text-lg sm:text-xl text-gray-800 mb-6 sm:mb-8 leading-normal">
              {description2}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {statsList.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-4 sm:p-6 text-center shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-green-50 transition-all duration-500 group cursor-pointer hover:border-emerald-200"
                >
                  <div
                    className={`text-2xl sm:text-3xl font-bold ${stat.color} mb-2 group-hover:scale-110 transition-transform duration-300`}
                  >
                    {stat.value}
                  </div>
                  <div className="text-gray-700 text-xs sm:text-sm font-medium group-hover:text-gray-800 transition-colors duration-300">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {shouldShowButton && (
              <button
                onClick={handleButtonClick}
                className="w-full sm:w-auto px-4 py-3 sm:px-8 sm:py-4 h-11 text-base sm:text-lg font-semibold btn-primary-gradient hover:btn-primary-gradient text-white transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40 transform hover:-translate-y-1 inline-flex items-center justify-center gap-2 rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                data-testid="button-find-high-impact-locations"
              >
                Find Your High-Impact Locations →
              </button>
            )}
          </div>
        </div>
      </StandardSection>
    </>
  );
}